import io from 'socket.io-client';
import locationTracker, { LocationSubscribeFunctionType } from "../location";

export type SocketOptions = {
  url: string,
  onError?: (error: Error) => void,
  token?: string
}

export type SocketStateType<T extends {}> = {
  connecting: boolean,
  connected: boolean,
  error: string,
  data: T
}

type SubscribeFunctionType<T> = (state: SocketStateType<T>, socket: BaseSocket<T>) => void;

type StateUpdateFunctionType<T extends {}> =
  (prevState: SocketStateType<T>) => Partial<SocketStateType<T>>

type SetStateType<T extends {}> =
  (nextState: Partial<SocketStateType<T>> | StateUpdateFunctionType<T>) => void

const initialState: SocketStateType<any> = {
  connecting: false,
  connected: false,
  error: '',
  data: {}
};

class BaseSocket<T extends {}> {
  requestFrom: string;
  url: string;
  token?: string;
  socket?: SocketIOClient.Socket;
  onError?: (error: Error) => void;
  state: SocketStateType<T>;
  _listeners: Map<string, Set<Function>>;
  _subscribers: Set<SubscribeFunctionType<T>>;
  _unsubscribeLocation?: () => void;

  constructor({ requestFrom, ...opts } : SocketOptions & { requestFrom: string }) {
    this.requestFrom = requestFrom;
    this.state = initialState;
    this._listeners = new Map<string, Set<Function>>();
    this._subscribers = new Set<SubscribeFunctionType<T>>();
    this.onError = (...args) => console.error(...args);
    this.on('reconnecting', () =>
      this.setState({ connected: false, connecting: true }))
    this.on('connect', () =>
      this.setState({ connected: true, connecting: false }))
    this.on('reconnect', () =>
      this.setState({ connected: true, connecting: false }))
    this.on('disconnect', () =>
      this.setState({ connected: false, connecting: false }))
    this.on('connect_error', (error: string) =>
      this.setState({ connected: false, connecting: false, error }))
    this.on('reconnect_failed', (error: string) =>
      this.setState({ connected: false, connecting: false, error }))
    this.on('error', (error: Error) =>
      this.onError && this.onError(error));
    this.update(opts);
  }

  update = ({ url, onError, token} : SocketOptions) => {
    this.onError = onError;
    if (this.url !== url || this.token !== token) {
      if (this.socket) {
        this.disconnect();
      }
      this.url = url;
      this.token = token;
      if (this.url && this.token) {
        this.connect();
      }
    }
  }

  on = (event: string, listener: Function) => {
    if (!this._listeners.has(event)) {
      this._listeners.set(event, new Set<Function>());
    }
    const eventListeners = this._listeners.get(event) as Set<Function>;
    if (eventListeners.has(listener)) this.throw("Listener is already there");
    eventListeners.add(listener);
    if (this.socket) {
      const socket = this.socket;
      // @ts-ignore
      this.socket.on(event, (...args) => {
        if (this.socket === socket) {
          return listener(...args);
        }
      })
    }
    return () => this.removeListener(event, listener);
  }

  emit = (event: string, ...args: any[]) => {
    if (!this.socket) {
      this.throw("Socket is not ready to emit!");
    }
    // TODO maybe check if socket is connected and throw if it is not
    // @ts-ignore
    return this.socket.emit(event, ...args);
  }

  removeListener = (event: string, listener: Function) => {
    if (!this._listeners.has(event)) this.throw("Listener, and even event is not there");
    const eventListeners = this._listeners.get(event) as Set<Function>;
    if (!eventListeners.has(listener)) this.throw("Listener is not there");
    eventListeners.delete(listener);
    if (eventListeners.size === 0) {
      this._listeners.delete(event);
    }
    if (this.socket) {
      this.socket.removeEventListener(event, listener);
    }
  }

  connect = () => {
    if (this.socket) this.throw("There is an existing socket, already");
    const queryToken = this.token || this.throw(`Missing option "token"`);
    // @ts-ignore
    const socket: SocketIOClient.Socket = this.socket = io(this.url, {
      forceNew: true,
      query: `token=${queryToken}&requestFrom=${this.requestFrom}`,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1 * 1000,
      reconnectionDelayMax: 10 * 1000,
      autoConnect: true,
      rejectUnauthorized: true
    });
    this._listeners.forEach((eventListeners, event) => {
      eventListeners.forEach((listener) => {
        // @ts-ignore
        socket.on(event, (...args) => {
          if (this.socket === socket) {
            return listener(...args)
          }
        })
      })
    })
    this._unsubscribeLocation = locationTracker.subscribe(
      (...args) => this.onLocationUpdate(...args)
    )
  }

  onLocationUpdate: LocationSubscribeFunctionType = (/*location, timestamp*/) => {
    console.warn('onLocationUpdate is not implemented on base socket.');
  }

  disconnect = () => {
    if (!this.socket) this.throw("There is not any socket to disconnect");
    // @ts-ignore
    this.socket.disconnect(true)
    this.socket = undefined;
    if (this._unsubscribeLocation) {
      this._unsubscribeLocation();
    }
  }

  setState: SetStateType<T> = (nextState) => {
    if (typeof nextState === 'function') {
      return this.setState(nextState(this.state));
    }
    this.state = { ...this.state, ...nextState };
    this._subscribers.forEach((subscriber: SubscribeFunctionType<T>) => {
      subscriber(this.state, this);
    })
  }

  subscribe = (subscriber: SubscribeFunctionType<T>) => {
    if(this._subscribers.has(subscriber)) this.throw("Already subscribed");
    this._subscribers.add(subscriber);
    const unsubscribe = () => {
      if (!this._subscribers.has(subscriber)) this.throw("Already unsubscribed");
      this._subscribers.delete(subscriber);
    }
    /*subscriber(this.state, this);*/
    return unsubscribe;
  }

  throw = (message: string) => {
    throw new Error(`ERROR IN SOCKET<${this.requestFrom}>: ${message}`);
  }
}

export default BaseSocket;
